import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Autocomplete,
    TextField,
    Button,
    CircularProgress,
    Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';
import { Industry } from '../../../models/Industry';
import { getIndustries } from '../../../services/industryService';

interface City {
    id: number;
    name: string;
    region: string;
    country: string;
    formatted: string;
}

interface SearchBarProps {
    onSearch: (industry: string, location: string) => void;
    isLoading?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch, isLoading = false }) => {
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [selectedIndustry, setSelectedIndustry] = useState<string>('');
    const [selectedCity, setSelectedCity] = useState<City | null>(null);
    const [loadingCities, setLoadingCities] = useState(false);
    const [loadingIndustries, setLoadingIndustries] = useState(false);

    // Cache for city search results
    const citiesCache = useRef<Record<string, City[]>>({});

    // Load industries from Firebase
    useEffect(() => {
        const loadIndustries = async () => {
            setLoadingIndustries(true);
            try {
                const data = await getIndustries();
                setIndustries(data);
            } catch (error) {
                console.error('Error loading industries:', error);
            } finally {
                setLoadingIndustries(false);
            }
        };
        loadIndustries();
    }, []);

    // Debounced function to fetch cities
    const fetchCities = debounce(async (query: string) => {
        if (!query || query.length < 2) {
            setCities([]);
            return;
        }

        // Check cache first
        if (citiesCache.current[query]) {
            setCities(citiesCache.current[query]);
            return;
        }

        setLoadingCities(true);
        try {
            const response = await fetch(
                `https://wft-geo-db.p.rapidapi.com/v1/geo/cities?namePrefix=${query}&limit=10&sort=-population&countryIds=US`,
                {
                    method: 'GET',
                    headers: {
                        'X-RapidAPI-Key': process.env.REACT_APP_RAPIDAPI_KEY || '',
                        'X-RapidAPI-Host': 'wft-geo-db.p.rapidapi.com'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data && Array.isArray(data.data)) {
                const formattedCities: City[] = data.data.map((city: any) => ({
                    id: city.id,
                    name: city.city,
                    region: city.region,
                    country: city.country,
                    formatted: `${city.city}, ${city.region}`
                }));
                // Store in cache
                citiesCache.current[query] = formattedCities;
                setCities(formattedCities);
            } else {
                console.error('Invalid API response format:', data);
                setCities([]);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    }, 800); // Increased debounce to 800ms

    const handleSearch = () => {
        if (selectedIndustry && selectedCity) {
            onSearch(selectedIndustry, selectedCity.formatted);
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Autocomplete
                    fullWidth
                    options={industries}
                    getOptionLabel={(option) => option.name}
                    loading={loadingIndustries}
                    onChange={(_, value) => setSelectedIndustry(value?.name || '')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Industry"
                            placeholder="e.g., Plumber, Restaurant"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingIndustries ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <Autocomplete
                    fullWidth
                    options={cities}
                    getOptionLabel={(option) => option.formatted}
                    loading={loadingCities}
                    onChange={(_, value) => setSelectedCity(value)}
                    onInputChange={(_, value) => fetchCities(value)}
                    componentsProps={{
                        popper: {
                            sx: {
                                zIndex: 1301,
                            }
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="City"
                            placeholder="e.g., Austin, TX"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={!selectedIndustry || !selectedCity || isLoading}
                    sx={{ px: 4, height: { xs: 40, sm: 56 } }}
                    startIcon={<SearchIcon />}
                >
                    {isLoading ? 'Searching...' : 'Search'}
                </Button>
            </Stack>
        </Box>
    );
};

export default SearchBar; 